import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { usePaymentMode } from '../contexts/PaymentModeContext';
import { 
  Calculator, 
  DollarSign, 
  Calendar, 
  Car, 
  TrendingUp,
  ChevronRight,
  Info,
  Sparkles
} from 'lucide-react';

const VEHICLE_TYPES = [
  { value: 'sedan', label: 'Sedan', suggestedPrice: 65 },
  { value: 'suv', label: 'SUV', suggestedPrice: 85 },
  { value: 'ute', label: 'Ute', suggestedPrice: 95 },
  { value: 'van', label: 'Van', suggestedPrice: 110 },
  { value: 'luxury', label: 'Luxury', suggestedPrice: 180 },
  { value: 'other', label: 'Other', suggestedPrice: 70 }
];

/**
 * HostEarningsCalculator
 * 
 * A public-facing tool for potential hosts to estimate earnings.
 * This is a marketing/conversion component, not analytics.
 * 
 * Uses the canonical platform fee from the backend.
 */
const HostEarningsCalculator = ({ 
  variant = 'default', // 'default' | 'compact' | 'dark'
  showHeading = true,
  className = ''
}) => {
  const navigate = useNavigate();
  const { isHostDirect } = usePaymentMode();
  const [vehicleType, setVehicleType] = useState('sedan');
  const [dailyPrice, setDailyPrice] = useState(65);
  const [bookedDays, setBookedDays] = useState(12);
  const [platformFeePercent, setPlatformFeePercent] = useState(5);  // Default 5% host fee
  const [hasInteracted, setHasInteracted] = useState(false);

  // Fetch current platform fee from backend - use HOST fee only
  useEffect(() => {
    const fetchPlatformFee = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platform-settings/fee`);
        if (res.ok) {
          const data = await res.json();
          // SPLIT FEE MODEL: Host calculator should use HOST fee only, not total
          setPlatformFeePercent(data.host_fee_percent || data.platform_fee_percentage || 15);
        }
      } catch (err) {
        // Use default 5% host fee if fetch fails
        setPlatformFeePercent(5);
      }
    };
    fetchPlatformFee();
  }, []);

  // Update suggested price when vehicle type changes
  useEffect(() => {
    const selected = VEHICLE_TYPES.find(v => v.value === vehicleType);
    if (selected && !hasInteracted) {
      setDailyPrice(selected.suggestedPrice);
    }
  }, [vehicleType, hasInteracted]);

  // Calculate earnings
  // PHASE B: In host_direct mode, there is no platform fee — net equals gross.
  const grossMonthly = dailyPrice * bookedDays;
  const platformFee = isHostDirect ? 0 : grossMonthly * (platformFeePercent / 100);
  const netMonthly = grossMonthly - platformFee;
  const annualEarnings = netMonthly * 12;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handlePriceChange = (value) => {
    setHasInteracted(true);
    setDailyPrice(Math.max(0, parseInt(value) || 0));
  };

  const handleDaysChange = (value) => {
    setHasInteracted(true);
    setBookedDays(Math.min(31, Math.max(0, parseInt(value) || 0)));
  };

  const isDark = variant === 'dark';

  return (
    <div className={`${className}`} data-testid="host-earnings-calculator">
      {/* Heading */}
      {showHeading && (
        <div className={`text-center mb-6 sm:mb-8 ${isDark ? 'text-white' : ''}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Earnings Calculator
          </div>
          <h2 className={`font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            See what your vehicle could earn
          </h2>
          <p className={`max-w-2xl mx-auto text-sm sm:text-base ${isDark ? 'text-white/80' : 'text-slate-600'}`}>
            Estimate your monthly and yearly earnings based on your vehicle type, pricing, and expected bookings.
          </p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-start">
        {/* Calculator Form */}
        <Card className={`${isDark ? 'bg-white/10 border-white/20' : 'bg-white border-slate-200'} shadow-lg rounded-2xl overflow-hidden`}>
          <CardContent className="p-5 sm:p-6 lg:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-10 h-10 ${isDark ? 'bg-white/20' : 'bg-[#004B8D]/10'} rounded-xl flex items-center justify-center`}>
                <Calculator className={`w-5 h-5 ${isDark ? 'text-white' : 'text-[#004B8D]'}`} />
              </div>
              <h3 className={`font-heading font-semibold text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Calculate Your Earnings
              </h3>
            </div>

            <div className="space-y-5">
              {/* Vehicle Type */}
              <div>
                <Label className={`text-sm font-medium mb-2 block ${isDark ? 'text-white/90' : 'text-slate-700'}`}>
                  <Car className="w-4 h-4 inline mr-1.5 opacity-70" />
                  Vehicle Type
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  {VEHICLE_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => {
                        setVehicleType(type.value);
                        setHasInteracted(false);
                      }}
                      className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        vehicleType === type.value
                          ? isDark 
                            ? 'bg-white text-[#004B8D]' 
                            : 'bg-[#004B8D] text-white'
                          : isDark
                            ? 'bg-white/10 text-white/80 hover:bg-white/20'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      data-testid={`vehicle-type-${type.value}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Daily Price */}
              <div>
                <Label className={`text-sm font-medium mb-2 block ${isDark ? 'text-white/90' : 'text-slate-700'}`}>
                  <DollarSign className="w-4 h-4 inline mr-1.5 opacity-70" />
                  Daily Price (AUD)
                </Label>
                <div className="relative">
                  <span className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-white/60' : 'text-slate-400'}`}>
                    $
                  </span>
                  <Input
                    type="number"
                    min="0"
                    max="1000"
                    value={dailyPrice}
                    onChange={(e) => handlePriceChange(e.target.value)}
                    className={`pl-8 h-12 text-lg font-medium rounded-xl ${
                      isDark 
                        ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40' 
                        : 'bg-white border-slate-200'
                    }`}
                    data-testid="daily-price-input"
                  />
                </div>
                <p className={`text-xs mt-1.5 ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
                  Suggested for {VEHICLE_TYPES.find(v => v.value === vehicleType)?.label}: ${VEHICLE_TYPES.find(v => v.value === vehicleType)?.suggestedPrice}/day
                </p>
              </div>

              {/* Booked Days */}
              <div>
                <Label className={`text-sm font-medium mb-2 block ${isDark ? 'text-white/90' : 'text-slate-700'}`}>
                  <Calendar className="w-4 h-4 inline mr-1.5 opacity-70" />
                  Expected Booked Days / Month
                </Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="range"
                    min="1"
                    max="31"
                    value={bookedDays}
                    onChange={(e) => handleDaysChange(e.target.value)}
                    className="flex-1 h-2 accent-[#004B8D]"
                    data-testid="booked-days-slider"
                  />
                  <div className={`w-16 h-12 flex items-center justify-center rounded-xl font-bold text-lg ${
                    isDark ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-900'
                  }`}>
                    {bookedDays}
                  </div>
                </div>
                <p className={`text-xs mt-1.5 ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
                  Average hosts book 10-15 days per month
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="space-y-4">
          {/* Main Result Card */}
          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 border-0 shadow-xl rounded-2xl overflow-hidden">
            <CardContent className="p-5 sm:p-6 lg:p-8 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-semibold text-lg">Estimated Earnings</h3>
              </div>

              <div className="space-y-4">
                {/* Net Monthly - Highlighted */}
                <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                  <p className="text-white/70 text-sm mb-1">Net Monthly Earnings</p>
                  <p className="text-3xl sm:text-4xl font-bold" data-testid="net-monthly-result">
                    {formatCurrency(netMonthly)}
                  </p>
                </div>

                {/* Breakdown */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                    <p className="text-white/70 text-xs mb-0.5">Gross Monthly</p>
                    <p className="text-lg font-semibold" data-testid="gross-monthly-result">
                      {formatCurrency(grossMonthly)}
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                    {isHostDirect ? (
                      <>
                        <p className="text-white/70 text-xs mb-0.5">Platform Fee</p>
                        <p
                          className="text-sm font-semibold italic"
                          data-testid="no-platform-fee-calculator"
                        >
                          No platform fee (direct payment mode)
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-white/70 text-xs mb-0.5">Host Service Fee ({platformFeePercent}%)</p>
                        <p className="text-lg font-semibold" data-testid="platform-fee-result">
                          -{formatCurrency(platformFee)}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {/* Annual */}
                <div className="border-t border-white/20 pt-4 mt-4">
                  <div className="flex items-center justify-between">
                    <p className="text-white/80">Estimated Annual Earnings</p>
                    <p className="text-2xl font-bold" data-testid="annual-result">
                      {formatCurrency(annualEarnings)}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <Card className={`${isDark ? 'bg-white/10 border-white/20' : 'bg-white border-slate-200'} shadow-lg rounded-2xl overflow-hidden`}>
            <CardContent className="p-5 sm:p-6">
              <Button
                onClick={() => navigate('/register')}
                className="w-full bg-[#004B8D] hover:bg-[#003a6d] text-white rounded-xl font-medium h-14 text-base sm:text-lg gap-2"
                data-testid="calculator-list-vehicle-cta"
              >
                List Your Vehicle
                <ChevronRight className="w-5 h-5" />
              </Button>
              <p className={`text-center text-sm mt-3 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                Create your listing and set your own price, availability, and rules.
              </p>
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <div className={`flex items-start gap-2 p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`} data-testid="earnings-disclaimer">
            <Info className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isDark ? 'text-white/50' : 'text-slate-400'}`} />
            <p className={`text-xs leading-relaxed ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
              <strong>Disclaimer:</strong> These figures are estimates only and do not constitute a guarantee of earnings. 
              Actual earnings depend on market demand, vehicle availability, your pricing strategy, booking frequency, 
              cancellations, trip completion, and other factors. HireHub Australia does not guarantee any specific level of income.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HostEarningsCalculator;
